<template>
  <component
    :is="tag"
    class="entity-list-item"
    :class="{
      'entity-list-item--clickable': isInteractive,
      'entity-list-item--danger': tone === 'danger',
      'entity-list-item--muted': muted,
    }"
    @click="handleClick"
  >
    <div v-if="$slots.icon || icon" class="entity-list-item__icon" :class="iconToneClass">
      <slot name="icon">
        <BaseIcon :name="icon" size="16" />
      </slot>
    </div>

    <div class="entity-list-item__content">
      <div class="entity-list-item__title-row">
        <span class="entity-list-item__title">
          <slot name="title">{{ title }}</slot>
        </span>
        <slot name="title-meta" />
      </div>
      <div v-if="$slots.subtitle || subtitle" class="entity-list-item__sub">
        <slot name="subtitle">{{ subtitle }}</slot>
      </div>
    </div>

    <div v-if="$slots.right" class="entity-list-item__right">
      <slot name="right" />
    </div>

    <div v-if="$slots.actions" class="entity-list-item__actions" @click.stop>
      <slot name="actions" />
    </div>

    <BaseIcon
      v-if="showChevron"
      name="chevron"
      size="14"
      class="entity-list-item__chevron"
    />
  </component>
</template>

<script>
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    name: 'EntityListItem',

    components: { BaseIcon },

    props: {
      title: { type: String, default: '' },
      subtitle: { type: String, default: '' },
      icon: { type: String, default: null },
      iconTone: {
        type: String,
        default: 'default',
        validator: (v) =>
          [
            'default',
            'brand',
            'sky',
            'amber',
            'green',
            'red',
            'violet',
            'slate',
          ].includes(v),
      },
      to: { type: [String, Object], default: null },
      tag: { type: String, default: 'div' },
      clickable: { type: Boolean, default: false },
      chevron: { type: Boolean, default: null },
      tone: { type: String, default: 'default' },
      muted: { type: Boolean, default: false },
    },

    emits: ['click'],

    setup(props, { emit }) {
      const router = useRouter();

      const isInteractive = computed(() => Boolean(props.to || props.clickable));

      const showChevron = computed(() => {
        if (props.chevron === false) return false;
        if (props.chevron === true) return true;
        return isInteractive.value;
      });

      const iconToneClass = computed(() => `entity-list-item__icon--${props.iconTone}`);

      const handleClick = (event) => {
        emit('click', event);
        if (props.to) {
          router.push(props.to);
        }
      };

      return { isInteractive, showChevron, iconToneClass, handleClick };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .entity-list-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--slate-100);
    transition: background-color 0.15s ease;
    text-decoration: none;
    color: inherit;

    &:last-child {
      border-bottom: none;
    }

    &--clickable {
      cursor: pointer;

      @include hover {
        background-color: var(--slate-50);
      }
    }

    &--danger {
      background-color: var(--red-50);

      &.entity-list-item--clickable {
        @include hover {
          background-color: var(--red-100);
        }
      }
    }

    &--muted {
      opacity: 0.6;
    }

    &__icon {
      width: 32px;
      height: 32px;
      border-radius: 0.375rem;
      background: var(--slate-100);
      color: var(--slate-500);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &--brand {
        background: var(--brand-50);
        color: var(--brand-600);
      }

      &--sky {
        background: var(--sky-50, var(--blue-50));
        color: var(--sky-600, var(--blue-600));
      }

      &--amber {
        background: var(--amber-50);
        color: var(--amber-600);
      }

      &--green {
        background: var(--green-50);
        color: var(--green-600);
      }

      &--red {
        background: var(--red-50);
        color: var(--red-600);
      }

      &--violet {
        background: var(--violet-50, var(--brand-50));
        color: var(--violet-600, var(--brand-600));
      }

      &--slate {
        background: var(--slate-100);
        color: var(--slate-600);
      }
    }

    &__content {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    &__title-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
    }

    &__title {
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--slate-900);
      @include ellipsis(1);
      min-width: 0;
    }

    &__sub {
      font-size: 0.75rem;
      color: var(--slate-500);
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex-wrap: wrap;
      line-height: 1.4;
    }

    &__right {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      flex-shrink: 0;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex-shrink: 0;
    }

    &__chevron {
      color: var(--slate-400);
      flex-shrink: 0;
      transform: rotate(90deg);
    }
  }
</style>
